import { z } from 'zod';
import { zahlstatusSchema, zahlwegSchema } from './offene-posten.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Offene Verbindlichkeiten je Kreditor und Zahlstand je Eingangsrechnung —
 * Auftrag `V-kreditorposten`, Auftraggeberwunsch vom 2026-09-02
 * („Teilzahlungen auf Eingangsrechnungen müssen möglich und sichtbar sein").
 *
 * **Der Zwilling von `offene-posten.ts`, und er ist NICHT sein Spiegelbild.**
 * Auf der Kundenseite ist die Forderung eine Journalzahl: Die Belegbuchung
 * einer ausgestellten Rechnung schreibt den Bruttobetrag ins Soll des
 * Forderungskontos (`document_posting_account`, `purpose = 'receivable'`), und
 * `offene-posten.query.ts` liest genau diese Zeile. Auf der Lieferantenseite
 * gibt es diese Zeile **nicht**, und das ist gemessen und nicht vermutet:
 *
 * | Gesucht | Befund am 2026-09-02 |
 * |---|---|
 * | Ein Kreditorenkonto in `document_posting_account` | Die Prüfbedingung `document_posting_account_purpose` (Migration `0022`) lässt genau `receivable`, `vat_not_due`, `vat_due`, `rounding` und seit `0290` `input_vat` zu. Es gibt kein `payable`. |
 * | Eine Erfassungsbuchung der Eingangsrechnung | `incoming_invoice.journal_entry_id` (Migration `0045`) trägt im DDL selbst den Kommentar „Stufe B, bleibt hier leer". Kein Codepfad schreibt die Spalte; `eingangsrechnungen/unbooked.report.ts` meldet deshalb für JEDEN freigegebenen Beleg den Grund `stufe_b_fehlt`. |
 *
 * **Daraus folgt die Aufteilung dieser Ableitung, und sie ist der ehrliche
 * Teil dieses Vertrags:**
 *
 * 1. **Die Verbindlichkeit ist der ERFASSTE Bruttobetrag**
 *    (`incoming_invoice.gross_total`) und keine Journalzahl. Sie ist keine
 *    zweite Wahrheit — sie ist die einzige, die es gibt: Was das Unternehmen
 *    dem Lieferanten schuldet, steht auf dessen Rechnung und nirgendwo sonst,
 *    solange Stufe B nicht bucht.
 * 2. **Das Bezahlte ist eine Journalzahl** — die Summe der Gegenseite jeder
 *    gebuchten, noch gültigen Kontierung, die auf diese Eingangsrechnung zeigt
 *    (`kontierung.matched_incoming_invoice_id`). Ein `SUM(...)`, keine
 *    gespeicherte Spalte, kein zweiter Schreibweg.
 * 3. **`offen` ist die Differenz**, und `status` kommt aus derselben reinen
 *    Funktion `zahlstatusAus()` wie auf der Kundenseite. Es gibt genau eine
 *    Stelle in diesem Repository, die aus zwei Beträgen `offen`/`teilbezahlt`/
 *    `bezahlt` macht, und eine zweite wäre die Drift, die als Erstes auffiele
 *    und als Letztes bemerkt würde.
 *
 * **Warum die Gegenseite und nicht das Geldkonto.** Der Buchungspfad
 * (`kontierung/buchung.ts`) legt bei einer einbehaltenen Anbietergebühr das
 * Geldkonto über NETTO und `4970` über die Gebühr an; die Position trägt dann
 * einen anderen Betrag als das Geldkonto. Die Position ist die einzige Zeile
 * der Buchung, die etwas über die RECHNUNG aussagt — das Geldkonto sagt nur,
 * wie viel Geld bewegt wurde. Diese Ableitung summiert deshalb alle Zeilen der
 * Buchung AUSSER dem Geldkonto der Bewegung (`bank_account.gl_account_id`,
 * gelesen und nicht geraten) und `4970`. Die Gebührenformel des Buchungspfades
 * ist dabei an eingehendem Geld ausgerichtet und für einen Geldabgang mit
 * einbehaltener Gebühr um die Gebühr daneben; der Fall tritt heute nicht auf,
 * und die Rechnung dazu steht im Kopfkommentar von
 * `apps/api/src/berichte/offene-verbindlichkeiten.query.ts`.
 *
 * **Was sie NICHT sehen kann, benannt statt verschwiegen:**
 *
 * - Eine Zahlung ohne Kontierung oder ohne Freigabe trägt keine Buchung. Die
 *   Rechnung erscheint als offen, und im Journal ist sie es auch.
 * - Eine kontierte Zahlung OHNE `matched_incoming_invoice_id` — etwa eine
 *   Sammelüberweisung über mehrere Rechnungen — senkt kein Rechnungskonto
 *   dieses Berichts. Sie erscheint in keiner Zeile, weil ihr der Bezug fehlt;
 *   eine erfundene Aufteilung wäre genau die Sorte Erfindung, die `A-K7`
 *   („erkannt und nicht verteilt") ausdrücklich verbietet.
 * - Eine **Gutschrift** des Lieferanten (`incoming_invoice.type =
 *   'credit_note'`) ist keine Zahlung. Sie steht hier gar nicht, aus demselben
 *   Grund, aus dem eine Ausgangs-Gutschrift keinen offenen Posten begründet.
 * - Ein Storno der Kontierung (`KontierungService.stornieren()`) nimmt
 *   Urbuchung und Gegenbuchung gemeinsam aus dem Bezug: Die Bewegung verliert
 *   `journal_entry_id`, und die neue gültige Kontierungszeile trägt
 *   `matched_incoming_invoice_id = NULL`. Die Rechnung ist danach wieder offen
 *   — kein Sonderweg, sondern die Folge derselben Bedingung.
 *
 * **Es entsteht keine neue Spalte und kein neuer Zustand.** Dieselbe
 * Entscheidung wie bei `offene-posten.ts`: `incoming_invoice_status`
 * (Migration `0043`) führt fünf Werte, und eine bezahlte Rechnung bleibt
 * `approved` beziehungsweise `captured`. Bezahlt zu sein ist keine Stufe des
 * Erfassungsverfahrens.
 */
/**
 * Eine einzelne Zahlung auf eine Eingangsrechnung.
 *
 * `betrag` ist vorzeichenbehaftet und positiv, wenn sie tilgt — dieselbe
 * Lesart wie `RechnungZahlung.betrag` auf der Kundenseite, nur mit der anderen
 * Blickrichtung: Dort ist eine Haben-Zeile auf dem Forderungskonto positiv,
 * hier eine Soll-Zeile auf der Gegenseite.
 */
export const verbindlichkeitZahlungSchema = z
    .object({
    journalEntryId: uuid,
    /** Die laufende Nummer der Buchung — die Anschrift im Journal, die ein Prüfer nennt. */
    entryNo: z.string(),
    bookingDate: isoDate,
    betrag: centAmount,
    /**
     * Woher das Geld kam. `guthaben` kommt hier nicht vor — eine
     * Eingangsrechnung wird nicht aus Kundenguthaben bezahlt —, der Wert
     * bleibt trotzdem im gemeinsamen Aufzählungstyp, weil eine zweite,
     * abgeschriebene Zahlwegliste genau die Drift wäre, die dieser Vertrag
     * vermeidet.
     */
    zahlweg: zahlwegSchema,
    bankTransactionId: uuid,
    beschreibung: z.string(),
})
    .strict();
/**
 * Der Zahlstand einer Eingangsrechnung OHNE ihre Einzelzahlungen — die Form,
 * in der die Eingangsrechnungsliste ihn spaltenweise zeigt.
 *
 * `verbindlichkeit` ist `incoming_invoice.gross_total` und ausdrücklich keine
 * Journalzahl; die Begründung steht im Kopfkommentar dieser Datei.
 */
export const eingangsrechnungZahlstandSchema = z
    .object({
    incomingInvoiceId: uuid,
    invoiceNo: z.string().nullable(),
    vendorId: uuid,
    vendorName: z.string(),
    /** Die Kreditorennummer (`vendor.creditor_no`) — die Anschrift, unter der ein Steuerberater sucht. */
    creditorNo: z.string(),
    invoiceDate: isoDate,
    dueDate: isoDate.nullable(),
    /**
     * Der erfasste Bruttobetrag — bei einer aus einem Vorsystem übernommenen
     * Eingangsrechnung ihr offener Betrag zum Stichtag
     * (`incoming_invoice.vorsystem_offen_stichtag`, `0252`); eine dort
     * erledigte steht in keiner Zeile.
     */
    verbindlichkeit: centAmount,
    bezahlt: centAmount,
    offen: centAmount,
    status: zahlstatusSchema,
    /**
     * Tage seit dem Zahlungsziel, oder `null` — wenn kein `dueDate` gesetzt
     * ist, wenn es noch nicht überschritten ist, oder wenn nichts mehr offen
     * ist. Dieselbe Funktion `tageUeberfaelligAus()` wie auf der Kundenseite.
     *
     * **Keine Mahnstufen-Vorschau.** Die Stufen der Mahnwesen-Spec gelten dem,
     * was UNS jemand schuldet. Eine Vorschau darauf, wie ein Lieferant uns
     * mahnen würde, wäre eine erfundene Fremdfrist.
     */
    tageUeberfaellig: z.number().int().nonnegative().nullable(),
})
    .strict();
/** Der Zahlstand samt Teilzahlungsliste — die Form der Erfassungsmaske. */
export const eingangsrechnungZahlstatusSchema = eingangsrechnungZahlstandSchema
    .extend({ zahlungen: z.array(verbindlichkeitZahlungSchema) })
    .strict();
/** Die älteste noch offene Eingangsrechnung eines Kreditors. */
export const offeneVerbindlichkeitAeltesteSchema = z
    .object({
    incomingInvoiceId: uuid,
    invoiceNo: z.string().nullable(),
    invoiceDate: isoDate,
    dueDate: isoDate.nullable(),
    offen: centAmount,
    tageUeberfaellig: z.number().int().nonnegative().nullable(),
})
    .strict();
/**
 * Ein Kreditor mit offener Verbindlichkeit.
 *
 * **Nur EINE Summe, und das ist der Unterschied zur Kundenseite.** Dort stehen
 * `saldo` (Kontostand) und `offenAusRechnungen` (abgeleiteter Rechnungsstand)
 * nebeneinander, weil beide existieren und auseinanderlaufen können. Hier gibt
 * es keinen Kreditorenkontostand, gegen den sich etwas halten liesse — Stufe B
 * bucht nicht. Ein Feld `nichtZugeordnet` stünde deshalb für eine Differenz,
 * die niemand messen kann, und wäre in jedem Bericht `0,00`: eine Zahl, die
 * Vollständigkeit behauptet, statt sie zu belegen.
 */
export const offeneVerbindlichkeitKreditorSchema = z
    .object({
    vendorId: uuid,
    vendorName: z.string(),
    creditorNo: z.string(),
    /** Die Summe der offenen Beträge aller Eingangsrechnungen dieses Kreditors. */
    offen: centAmount,
    /** Wie viele seiner Rechnungen `offen` oder `teilbezahlt` sind. */
    anzahlOffen: z.number().int().nonnegative(),
    /** Wie viele davon ihr Zahlungsziel überschritten haben. */
    anzahlUeberfaellig: z.number().int().nonnegative(),
    aelteste: offeneVerbindlichkeitAeltesteSchema.nullable(),
})
    .strict();
/** `GET /api/v1/berichte/offene-verbindlichkeiten` — je Kreditor, mit Rechnungszeilen. */
export const offeneVerbindlichkeitenBerichtSchema = z
    .object({
    stichtag: isoDate,
    /** Die Summe über alle Kreditoren. */
    summe: centAmount,
    kreditoren: z.array(offeneVerbindlichkeitKreditorSchema),
    /** Die noch offenen Rechnungszeilen, über alle Kreditoren, älteste zuerst. */
    rechnungen: z.array(eingangsrechnungZahlstandSchema),
    /** Die benannten Grenzen der Ableitung, im Bericht selbst. */
    grenzen: z.array(z.string()),
})
    .strict();
/** `GET /api/v1/berichte/verbindlichkeit-zahlstatus` — der Zahlstand erfasster Eingangsrechnungen. */
export const verbindlichkeitZahlstatusListeSchema = z
    .object({ rechnungen: z.array(eingangsrechnungZahlstandSchema) })
    .strict();
/**
 * `GET /api/v1/berichte/verbindlichkeit-zahlstatus/:incomingInvoiceId`.
 * `null`, wenn der Beleg keine erfasste Eingangsrechnung ist.
 */
export const verbindlichkeitZahlstatusAntwortSchema = z
    .object({ zahlstatus: eingangsrechnungZahlstatusSchema.nullable() })
    .strict();
//# sourceMappingURL=offene-verbindlichkeiten.js.map